/**
 * Acople borde-a-borde estilo ventanas (Windows):
 * solo cuando los bordes se tocan / están a pocos px, con guía visual.
 */

export const EDGE_SNAP_PX = 14
export const EDGE_GAP = 8
export const SNAP_GUIDE_ID = 'udv-panel-snap-guide'

/**
 * @param {number} x
 * @param {number} y
 * @param {number} w
 * @param {number} h
 */
export function rectFromBox(x, y, w, h) {
  return {
    left: x,
    top: y,
    right: x + w,
    bottom: y + h,
    width: w,
    height: h,
  }
}

function yOverlap(a, b, pad) {
  return a.bottom >= b.top - pad && a.top <= b.bottom + pad
}

/**
 * Detecta si el panel móvil puede acoplarse al ancla por un borde vertical.
 * side = lado del ancla donde quedará el móvil ('left' | 'right').
 *
 * @returns {{
 *   side: 'left'|'right',
 *   x: number,
 *   y: number,
 *   guide: { left: number, top: number, height: number }
 * } | null}
 */
export function probeEdgeSnap(moving, anchor, snapPx = EDGE_SNAP_PX) {
  if (!moving || !anchor) return null
  if (!yOverlap(moving, anchor, snapPx + 8)) return null

  const distLeft = Math.abs(moving.right - anchor.left)
  const distRight = Math.abs(moving.left - anchor.right)

  let side = null
  let dist = Infinity
  if (distLeft <= snapPx && distLeft <= distRight) {
    side = 'left'
    dist = distLeft
  } else if (distRight <= snapPx) {
    side = 'right'
    dist = distRight
  }
  if (!side) return null

  const y = Math.round(
    Math.min(
      Math.max(4, anchor.top),
      Math.max(4, moving.top)
    )
  )
  // Alinear al borde del ancla (el móvil “encaja”)
  const x =
    side === 'left'
      ? Math.round(anchor.left - moving.width - EDGE_GAP)
      : Math.round(anchor.right + EDGE_GAP)

  const guideTop = Math.min(moving.top, anchor.top)
  const guideBottom = Math.max(moving.bottom, anchor.bottom)
  const guideLeft =
    side === 'left'
      ? Math.round(anchor.left - EDGE_GAP / 2 - 1)
      : Math.round(anchor.right + EDGE_GAP / 2 - 1)

  return {
    side,
    x: Math.max(4, x),
    y,
    dist,
    guide: {
      left: guideLeft,
      top: Math.round(guideTop),
      height: Math.max(24, Math.round(guideBottom - guideTop)),
    },
  }
}

/**
 * Cuando el ancla es el cine y el móvil es DSA, side = dock del DSA.
 * Cuando el móvil es cine y el ancla es DSA, invertimos: side del DSA
 * es el opuesto al lado del cine respecto al DSA.
 */
export function probeDsaBesideCine(dsaBox, cineBox) {
  return probeEdgeSnap(dsaBox, cineBox)
}

export function probeCineBesideDsa(cineBox, dsaBox) {
  const hit = probeEdgeSnap(cineBox, dsaBox)
  if (!hit) return null
  // Cine encajado a la izquierda del DSA → el DSA queda a la derecha del cine
  const dsaSide = hit.side === 'left' ? 'right' : 'left'
  const groupX =
    dsaSide === 'left'
      ? Math.round(hit.x - dsaBox.width - EDGE_GAP)
      : Math.round(hit.x)
  return {
    dsaSide,
    x: Math.max(4, groupX),
    y: hit.y,
    guide: hit.guide,
  }
}

/** Guía visual sutil (línea luminosa entre paneles). */
export function updateSnapGuide(guide) {
  if (typeof document === 'undefined') return
  let el = document.getElementById(SNAP_GUIDE_ID)
  if (!guide) {
    if (el) el.style.opacity = '0'
    return
  }
  if (!el) {
    el = document.createElement('div')
    el.id = SNAP_GUIDE_ID
    el.setAttribute('aria-hidden', 'true')
    Object.assign(el.style, {
      position: 'fixed',
      width: '3px',
      borderRadius: '2px',
      pointerEvents: 'none',
      zIndex: '200000',
      background:
        'linear-gradient(180deg, transparent 0%, #81C784 12%, #A5D6A7 50%, #81C784 88%, transparent 100%)',
      boxShadow: '0 0 10px rgba(129,199,132,0.85), 0 0 2px rgba(165,214,167,0.9)',
      transition: 'opacity 0.12s ease, left 0.06s ease, top 0.06s ease, height 0.06s ease',
      opacity: '0',
    })
    document.body.appendChild(el)
  }
  el.style.left = `${guide.left}px`
  el.style.top = `${guide.top}px`
  el.style.height = `${guide.height}px`
  el.style.opacity = '1'
}

export function clearSnapGuide() {
  updateSnapGuide(null)
}

export function queryCineRect() {
  try {
    const cine =
      document.querySelector(
        '[data-linked-panels="true"] [data-cine-controls="true"]'
      ) || document.querySelector('[data-cine-controls="true"]')
    if (!cine) return null
    const r = cine.getBoundingClientRect()
    if (r.width < 40 || r.height < 20) return null
    return {
      left: r.left,
      top: r.top,
      right: r.right,
      bottom: r.bottom,
      width: r.width,
      height: r.height,
    }
  } catch (e) {
    return null
  }
}

export function queryDsaRect() {
  try {
    const dsa = document.querySelector('[data-dsa-cine-hud="true"]')
    if (!dsa) return null
    const r = dsa.getBoundingClientRect()
    if (r.width < 40 || r.height < 20) return null
    return {
      left: r.left,
      top: r.top,
      right: r.right,
      bottom: r.bottom,
      width: r.width,
      height: r.height,
    }
  } catch (e) {
    return null
  }
}

/** Posición free por defecto (arriba de la barra inferior, centrado-ish). */
export function defaultFreeCinePos() {
  const vw = typeof window !== 'undefined' ? window.innerWidth || 360 : 360
  const vh = typeof window !== 'undefined' ? window.innerHeight || 640 : 640
  return {
    dock: 'free',
    x: Math.max(8, Math.round(vw / 2 - 150)),
    y: Math.max(8, vh - 220),
  }
}

export function defaultFreeDsaPos() {
  const vw = typeof window !== 'undefined' ? window.innerWidth || 360 : 360
  const vh = typeof window !== 'undefined' ? window.innerHeight || 640 : 640
  return {
    dock: 'free',
    x: Math.max(8, Math.round(vw / 2 + 160)),
    y: Math.max(8, vh - 280),
  }
}
